import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import {
  Field,
  FieldArray,
  reduxForm,
  SubmissionError,
  change,
  formValueSelector,
} from "redux-form";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import LoadingProgress from '../components/LoadingProgress'

import ErrorMessage from "../components/ErrorMessage";
import ModuleHelpModal from "../components/ModuleHelpModal";
import { fetchModule, updateModule } from "../actions/modules";
import { renderInputField, renderButtonGroupField, renderCounterField } from "../utils/forms";
import { validateModuleForm, getValidLockerCounts, hasMissingSN } from "../utils/moduleFormValidation";
import {
  PREFIX_OPTIONS,
  MODE_TYPE_OPTIONS,
  MODE_OPTIONS,
  WIDE_OPTIONS,
  HARDTYPE_OPTIONS,
  GENDER_OPTIONS,
  ENVIRONMENT_OPTIONS,
} from "../utils/moduleFormOptions";
import '../styles/ModulesView.css'

import { QrReader } from "react-qr-reader";

const normalizeSerial = (value) => (value || '').replace(/\D/g, '').slice(0, 5);
const normalizeSN = (value) => (value || '').replace(/\D/g, '').slice(0, 10);
const normalizePin = (value) => (value || '').replace(/\D/g, '').slice(0, 4);

// Build the 8-char Code (prefix + 5-digit serial); the backend re-inserts the
// 2-digit locker count when it saves.
function buildCode(prefix, serial) {
  const p = prefix || 'VLM';
  const s = String(serial || '').replace(/\D/g, '').padStart(5, '0').slice(-5);
  return `${p}${s}`;
}

class ModuleEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      result: "Escaneando...",
      qrModalOpen: false,
      qrConfirmModalOpen: false,
      qrScanActivated: false,
      currentPairingIndex: 0,
      helpModalOpen: false,
      configCollapsed: typeof window !== 'undefined' && window.innerWidth <= 768,
      showPins: false,
    };

    this.submit = this.submit.bind(this);
    this.qrModalOpenToggle = this.qrModalOpenToggle.bind(this);
    this.qrConfirmModalOpenToggle = this.qrConfirmModalOpenToggle.bind(this);
    this.addPairingLine = this.addPairingLine.bind(this);
    this.finishPairing = this.finishPairing.bind(this);
    this.setLockerCount = this.setLockerCount.bind(this);
    this.toggleHelpModal = this.toggleHelpModal.bind(this);
    this.toggleConfig = this.toggleConfig.bind(this);
    this.toggleShowPins = this.toggleShowPins.bind(this);
    this.renderPairing = this.renderPairing.bind(this);
  }

  toggleHelpModal() {
    this.setState((prevState) => ({ helpModalOpen: !prevState.helpModalOpen }));
  }

  toggleConfig() {
    this.setState((prevState) => ({ configCollapsed: !prevState.configCollapsed }));
  }

  toggleShowPins() {
    this.setState((prevState) => ({ showPins: !prevState.showPins }));
  }

  setLockerCount(fields, n) {
    const current = fields.length;
    if (n > current) {
      for (let i = current; i < n; i++) fields.push({});
    } else if (n < current) {
      for (let i = current; i > n; i--) fields.pop();
    }
  }

  componentDidMount() {
    const { modules, fetchModule, match } = this.props;

    if (!modules.module) {
      fetchModule(match.params.Code);
    } else {
      this.setState({ loaded: true });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!this.state.loaded && nextProps.modules.module) {
      this.setState({ loaded: true });
    }
  }

  submit(values) {
    const { updateModule, modules } = this.props;

    const payload = { ...values, Code: buildCode(values.Prefix, values.Serial) };
    delete payload.Prefix;
    delete payload.Serial;

    return updateModule(modules.module.Code, payload).then((response) => {
      if (response.error) {
        throw new SubmissionError({
          _error: response.error.message,
          ...response.error.fields,
        });
      }

      return response;
    });
  }

  qrModalOpenToggle() {
    const isOpening = !this.state.qrModalOpen;

    this.setState((prevState) => ({
      qrModalOpen: !prevState.qrModalOpen,
      qrScanActivated: isOpening,
    }));

    if (isOpening) {
      this.clearRegisterLockers(); // 👈 Clear only when opening
    }
  }

  clearRegisterLockers() {
    this.props.change("ModuleEditForm", "RegisterLockers", []);
    this.setState({ currentPairingIndex: 0 });
  }

  qrConfirmModalOpenToggle() {
    this.setState((prevState) => ({
      qrConfirmModalOpen: !prevState.qrConfirmModalOpen,
    }));
  }

  addPairingLine(fields) {
    fields.push({});
    this.setState((prevState) => ({
      currentPairingIndex: prevState.currentPairingIndex + 1,
      qrScanActivated: true,
      result: null,
    }));
  }

  finishPairing() {
    this.setState({ qrModalOpen: false, qrScanActivated: false });
  }

  renderQRResult(fields) {
    return (
      <div>
        <div className="mt-5 mb-5 text-center">
          <span>SN escaneado</span>
          <br></br>
          <h5>{this.state.result}</h5>
        </div>
        <div className="flex justify-content-around mt-5">
          <Button color="danger" onClick={this.finishPairing}>
            Finalizar
          </Button>
          {!this.state.qrScanActivated && (
            <Button color="primary" onClick={() => this.addPairingLine(fields)}>
              Confirmar y seguir
            </Button>
          )}
        </div>
      </div>
    );
  }

  renderQRScan(fields) {
    const { currentPairingIndex } = this.state;

    return (
      <div>
        <QrReader
          onResult={(result, error) => {
            if (
              !!result &&
              this.state.qrScanActivated &&
              fields.get(currentPairingIndex)
            ) {
              fields.get(currentPairingIndex).SN = result?.text.slice(-8);
              this.setState({
                result: result?.text.slice(-8),
                qrScanActivated: false,
              });
            }

            if (!!error) {
              console.info(error);
            }
          }}
          style={{ width: "100%" }}
          constraints={{ facingMode: "environment" }}
        />
        <div className="flex justify-content-around mt-5">
          <Button color="danger" onClick={this.finishPairing}>
            Finalizar
          </Button>
        </div>
      </div>
    );
  }

  renderPairing({ fields, meta }) {
    const count = fields.length;
    const validCounts = getValidLockerCounts(this.props.prefix);
    const countValid = validCounts.includes(count);

    // Lockers display in 2 columns (except a single one), filled column-major
    // (top→bottom, then next column) to match the SN picking order.
    const twoCols = count > 1;
    const rows = twoCols ? Math.ceil(count / 2) : count;

    // "Añadir"/"Quitar" jump to the next/previous valid count
    // (e.g. 1→2→4→6→10→12 and back 12→10→6→4→2→1).
    const nextCount = validCounts.find(n => n > count);
    const lowerCounts = validCounts.filter(n => n < count);
    const prevCount = lowerCounts.length ? lowerCounts[lowerCounts.length - 1] : null;
    const atMax = nextCount == null;
    const atMin = prevCount == null;

    return (
      <div>
        {/* ── Locker toolbar: live count + quick-set ───────── */}
        <div className="module-locker-toolbar">
          <div className={`module-locker-counter ${countValid ? 'valid' : 'invalid'}`}>
            <i className={`fas ${countValid ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`}></i>
            {count} {count === 1 ? 'taquilla' : 'taquillas'}
          </div>
          <div className="module-quick-set">
            <span className="module-quick-set-label">Fijar nº:</span>
            {validCounts.map(n => (
              <button
                key={n}
                type="button"
                className={`module-quick-set-btn ${count === n ? 'active' : ''}`}
                onClick={() => this.setLockerCount(fields, n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {meta && meta.error && (
          <div className="module-array-error">
            <i className="fas fa-exclamation-triangle mr-2"></i>{meta.error}
          </div>
        )}

        {count === 0 ? (
          <div className="module-locker-empty">
            No hay taquillas. Usa los botones "Fijar nº" o "Añadir".
          </div>
        ) : (
          <>
            {twoCols && (
              <div className={`module-locker-colheads ${this.state.showPins ? 'pins-on' : 'pins-off'}`}>
                <span className="module-locker-colhead">Columna 1</span>
                <span className="module-locker-colhead">Columna 2</span>
              </div>
            )}
            <div
              className={`module-locker-grid ${twoCols ? 'two-col' : 'one-col'} ${this.state.showPins ? 'pins-on' : 'pins-off'}`}
              style={{ gridTemplateRows: `repeat(${Math.max(1, rows)}, auto)` }}
            >
              {fields.map((registerlocker, index) => (
                <div className="module-locker-item" key={index}>
                  <span className="module-locker-num">{index + 1}</span>
                  <span className="module-locker-tag">SN</span>
                  <Field
                    name={`${registerlocker}.SN`}
                    type="text"
                    component={renderInputField}
                    placeholder="Nº de serie"
                    normalize={normalizeSN}
                  />
                  {this.state.showPins && (
                    <>
                      <span className="module-locker-tag">PIN</span>
                      <Field
                        name={`${registerlocker}.Pin`}
                        type="text"
                        component={renderInputField}
                        placeholder="2903"
                        normalize={normalizePin}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="module-locker-actions">
          <button
            className="module-locker-add is-back"
            type="button"
            disabled={atMin}
            onClick={() => this.setLockerCount(fields, prevCount)}
          >
            <i className="fas fa-minus"></i>
            <span>{atMin ? 'Mínimo' : `Quitar (${prevCount})`}</span>
          </button>
          <button
            className="module-locker-add is-add"
            type="button"
            disabled={atMax}
            onClick={() => this.setLockerCount(fields, nextCount)}
          >
            <i className="fas fa-plus"></i>
            <span>{atMax ? 'Máximo' : `Añadir (${nextCount})`}</span>
          </button>
        </div>
        <div>
          <Modal
            isOpen={this.state.qrModalOpen}
            toggle={this.qrModalOpenToggle}
            className="centered-modal"
          >
            <ModalHeader toggle={this.qrModalOpenToggle}>
              Escaneando posición {this.state.currentPairingIndex + 1}...
            </ModalHeader>
            <ModalBody>
              {this.state.qrScanActivated
                ? this.renderQRScan(fields)
                : this.renderQRResult(fields)}
            </ModalBody>
            <ModalFooter></ModalFooter>
          </Modal>
        </div>
      </div>
    );
  }

  render() {
    const { modules, error, pristine, submitting, handleSubmit, registerLockers, prefix, serial } = this.props;
    const missingSN = hasMissingSN(registerLockers);

    const lockerCount = Array.isArray(registerLockers) ? registerLockers.length : 0;
    const previewPrefix = prefix || 'VLM';
    const previewSerial = (serial || '').replace(/\D/g, '').slice(0, 5);
    const previewCode = previewSerial
      ? `${previewPrefix}${String(lockerCount).padStart(2, '0')}${previewSerial.padStart(5, '0')}`
      : null;

    if (!this.state.loaded) {
      if (modules.error) {
        return <ErrorMessage message={modules.error.message} />;
      }
      return (
        <LoadingProgress />
      );
    }

    return (
      <form onSubmit={handleSubmit(this.submit)} className="module-form-view">

        {/* ── Header ───────────────────────────────── */}
        <div className="mach-admin-header">
          <div className="header-content">
            <div className="header-text">
              <h1>
                <i className="fas fa-edit mr-3"></i>
                <span className="text-vw-dark mr-2">Editar </span>
                <span className="text-vw-light">Módulo</span>
              </h1>
            </div>
            <div className="header-actions">
              <button
                className="mach-admin-btn secondary"
                type="button"
                onClick={() => window.history.back()}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Volver</span>
              </button>
              <button
                className="mach-admin-btn light"
                type="button"
                onClick={this.toggleHelpModal}
              >
                <i className="fas fa-info-circle"></i>
                <span>Ayuda</span>
              </button>
              <button
                className="mach-admin-btn warning"
                type="button"
                onClick={() => this.qrModalOpenToggle()}
              >
                <i className="fas fa-qrcode"></i>
                <span>Borrar y escanear QR</span>
              </button>
              <button
                className="mach-admin-btn primary"
                type="submit"
                disabled={pristine || submitting || missingSN}
                title={missingSN ? 'Introduce el SN de todas las taquillas para guardar' : undefined}
              >
                <i className="fas fa-save"></i>
                <span>Guardar</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Split: config (left) · taquillas (right) ─── */}
        <div className="module-form-split">

          {/* Left column: two stacked cards */}
          <div className="module-form-left">

            {/* Card 1 · Module name */}
            <div className="module-form-card">
              <div className="module-form-section-title">
                <i className="fas fa-tag mr-2"></i>Nombre del módulo
              </div>
              <div className="module-config-grid">
                <div className="module-config-cell">
                  <div className="module-code-builder">
                    <div className="module-code-main">
                      <label className="module-code-label">Nº de Serie *</label>
                      <div className="module-code-row">
                        <div className="module-code-prefix">
                          <Field name="Prefix" component={renderButtonGroupField} options={PREFIX_OPTIONS} />
                        </div>
                        <div className="module-code-serial">
                          <Field name="Serial" type="text" component={renderInputField}
                            placeholder="01234" normalize={normalizeSerial} />
                        </div>
                      </div>
                    </div>
                    <div className="module-code-resultgroup">
                      <label className="module-code-label">Resultado</label>
                      <div className="module-code-result">
                        <span className="module-code-result-val">{previewCode || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 · Configuration */}
            <div className="module-form-card">
              <div className={`module-form-section-title module-section-collapsible ${this.state.configCollapsed ? 'is-collapsed' : ''}`}
                onClick={this.toggleConfig}>
                <span><i className="fas fa-cubes mr-2"></i>Configuración</span>
                <button type="button" className="module-collapse-btn"
                  aria-expanded={!this.state.configCollapsed}>
                  <i className={`fas fa-${this.state.configCollapsed ? 'eye' : 'eye-slash'}`}></i>
                  <span>{this.state.configCollapsed ? 'Mostrar' : 'Ocultar'}</span>
                </button>
              </div>
              {!this.state.configCollapsed && (
              <div className="module-config-grid">
                <div className="module-config-cell">
                  <Field name="ModeType" component={renderButtonGroupField} label="Modo *"
                    options={MODE_TYPE_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="Mode" component={renderButtonGroupField} label="Formato *"
                    options={MODE_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="Wide" component={renderButtonGroupField} label="Ancho *"
                    options={WIDE_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="HardType" component={renderButtonGroupField} label="Cierre *"
                    options={HARDTYPE_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="Gender" component={renderButtonGroupField} label="Género"
                    options={GENDER_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="Environment" component={renderButtonGroupField} label="Entorno"
                    options={ENVIRONMENT_OPTIONS} />
                </div>
                <div className="module-config-cell">
                  <Field name="Espressif" component={renderCounterField} label="Espressif *"
                    min={0} max={15} />
                </div>
              </div>
              )}
            </div>

          </div>

          {/* Taquillas */}
          <div className="module-form-card">
            <div className="module-form-section-title module-section-header">
              <span><i className="fas fa-list mr-2"></i>Taquillas</span>
              <button type="button" className="module-collapse-btn" onClick={this.toggleShowPins}>
                <i className={`fas fa-${this.state.showPins ? 'eye-slash' : 'key'}`}></i>
                <span>{this.state.showPins ? 'Ocultar PIN' : 'Editar PIN'}</span>
              </button>
            </div>
            <FieldArray
              name="RegisterLockers"
              component={this.renderPairing}
            />
          </div>

        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <ModuleHelpModal isOpen={this.state.helpModalOpen} toggle={this.toggleHelpModal} />

      </form>
    );
  }
}

ModuleEditForm = reduxForm({
  form: "ModuleEditForm",
  enableReinitialize: true,
  validate: validateModuleForm,
  onSubmitSuccess: (result, dispatch, props) => {
    props.push(`/modules/${result.response.Code}`);
  },
})(ModuleEditForm);

const selector = formValueSelector('ModuleEditForm');

const mapStateToProps = (state) => {
  const module = state.modules.module || {};
  const code = module.Code || '';
  const prefixMatch = code.match(/^([A-Z]{3})/);
  const serialMatch = code.match(/(\d{1,5})$/); // trailing 5-digit serial

  return {
    modules: state.modules,
    prefix: selector(state, 'Prefix'),
    serial: selector(state, 'Serial'),
    registerLockers: selector(state, 'RegisterLockers'),
    initialValues: {
      ...module,
      Prefix: prefixMatch ? prefixMatch[1] : 'VLM',
      Serial: serialMatch ? serialMatch[1] : '',
      RegisterLockers: module.RegisterLockers || [],
    },
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchModule: bindActionCreators(fetchModule, dispatch),
  updateModule: bindActionCreators(updateModule, dispatch),
  push: bindActionCreators(push, dispatch),
  change: bindActionCreators(change, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModuleEditForm);
